<template>
      <div id="benefits">
            <section class="section intro-hero benefits-hero animate__animated animate__fadeIn">
                  <BackButton link="/plan-credits" />
                  <div class="grid-container max">
                        <div class="benefits-header">
                              <h1>2027 Anthem Health Plan Options</h1>
                              <p class="benefits-desc">Use the filters and dropdown menu below to compare plans side by side. Select your preferred coverage type, adjust the options to match your needs, and explore the differences in cost and benefits across each plan.</p>
                        </div>

                        <div ref="stickySentinel" class="benefits-sticky-sentinel"></div>

                        <div class="benefits-tabs">
                              <button v-for="tier in tiers" :key="tier.key" type="button" class="benefits-tab" :class="{ active: activeTier === tier.key }" @click="activeTier = tier.key">{{ tier.label }}</button>
                        </div>

                        <div class="benefits-plans-row-wrap" :class="{ 'is-stuck': isStuck }">
                        <div class="benefits-plans-row">
                              <button type="button" class="benefits-plan-pill" v-for="plan in plans" :key="plan.key" :class="{ active: activePlan === plan.label }" @click="planSelect(plan.label)">{{ plan.label }}</button>
                        </div>
                        </div>

                        <div class="benefits-table-scroll">
                        <div class="benefits-categories">
                              <transition name="table-anim" enter-active-class="animate__animated animate__fadeIn animate__faster" leave-active-class="animate__animated animate__fadeOut animate__faster" mode="out-in">
                                    <div :key="activeTier">
                                          <div class="benefits-category" v-for="category in categories" :key="category.title">
                                                <h3 class="benefits-category-title">{{ category.title }} <span v-if="category.tooltip" class="info-circle" @click="infoPop(category.title, category.tooltip)"></span></h3>
                                                <hr class="benefits-rule" />

                                                <template v-if="category.type === 'wellbeing'">
                                                      <div class="benefits-wellbeing-block" v-for="(wb, wIndex) in category.wellbeing" :key="wIndex">
                                                            <p class="benefits-metric-label" v-if="category.wellbeing.length > 1">{{ wb.label }}</p>
                                                            <div class="benefits-wellbeing-grid">
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.hmo }}</p>
                                                                        <p class="benefits-sub">HMO<sup>6</sup> (Completion of all 2027 well-being incentive actions)</p>
                                                                  </div>
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.goldBase }}</p>
                                                                        <p class="benefits-sub">Gold Base Contribution</p>
                                                                  </div>
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.goldTotal }}</p>
                                                                        <p class="benefits-sub">Gold Total (Gold base + completion of all 2027 well-being incentive actions)</p>
                                                                  </div>
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.silverBase }}</p>
                                                                        <p class="benefits-sub">Silver Base Contribution</p>
                                                                  </div>
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.silverTotal }}</p>
                                                                        <p class="benefits-sub">Silver Total (Silver base + completion of all 2027 well-being incentive actions)</p>
                                                                  </div>
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.bronzeBase }}</p>
                                                                        <p class="benefits-sub">Bronze Base Contribution</p>
                                                                  </div>
                                                                  <div class="benefits-wellbeing-item">
                                                                        <p class="benefits-value">{{ wb.bronzeTotal }}</p>
                                                                        <p class="benefits-sub">Bronze Total (Bronze base + completion of all 2027 well-being incentive actions)</p>
                                                                  </div>
                                                            </div>
                                                      </div>
                                                </template>

                                                <template v-else>
                                                      <div class="benefits-metric" v-for="(metric, mIndex) in category.metrics" :key="mIndex">
                                                            <p class="benefits-metric-label" v-if="category.metrics.length > 1">{{ metric.label }}</p>
                                                            <div class="benefits-grid">
                                                                  <div class="benefits-col" v-for="(plan, i) in plans" :key="plan.key">
                                                                        <template v-if="metric.type === 'pair'">
                                                                              <p class="benefits-value">{{ metric.in[i] }}<span class="benefits-sub">{{ metric.inLabel || 'in-network' }}</span></p>
                                                                              <p class="benefits-value">{{ metric.out[i] }}<span class="benefits-sub">{{ metric.outLabel || 'out-of-network' }}</span></p>
                                                                        </template>
                                                                        <template v-else-if="metric.type === 'stack'">
                                                                              <p class="benefits-value" v-for="(item, iIndex) in metric.columns[i]" :key="iIndex">{{ item.value }}<span class="benefits-sub">{{ item.label }}</span></p>
                                                                        </template>
                                                                        <template v-else>
                                                                              <p class="benefits-value">{{ metric.values[i] }}</p>
                                                                        </template>
                                                                  </div>
                                                            </div>
                                                            <p class="benefits-note" v-if="metric.note">{{ metric.note }}</p>
                                                      </div>
                                                </template>
                                          </div>
                                    </div>
                              </transition>
                        </div>
                        </div>

                        <div class="benefits-disclaimer">
                              <ol>
                                    <li>Out-of-network coverage is not available for the HMO plan except for emergency care. If you use an out-of-network provider on the HRA plans you could be balance billed.</li>
                                    <li>Pharmacy costs count toward your out-of-pocket maximum.</li>
                                    <li>Services must be properly coded as preventive care under the Patient Protection and Affordable Care Act and provided by an in-network doctor.</li>
                                    <li>Physician office services is defined as family practice, general practice, internal medicine, pediatrics and OB/GYN.</li>
                                    <li>Completing your well-being incentive actions with Sharecare will earn you points. You can choose to redeem these points as well-being incentive credits (indicated in this chart) to use on covered medical and pharmacy expenses or the Visa Prepaid Card option. Visit BeWellSHBP.com for more information.</li>
                                    <li>If you are on the HMO plan and redeem your points as credits, they will go into your MyIncentive Account which is a standalone account offered alongside the HMO plan.</li>
                              </ol>
                              <p class="benefits-fineprint">This is a high-level summary of benefits. It does not describe all benefits and does not describe exclusions and limitations. The plan documents posted on www.shbp.georgia.gov include the full details. Dollar amounts, visit limitations, medical co-pays (HMO), co-insurance, deductible and out-of-pocket limits are based on January 1 - December 31, 2027, plan year. All covered medical services are subject to deductible except where noted. Note: Medical co-pays (HMO) do not count toward the deductible but do count toward the out-of-pocket maximum.</p>
                        </div>
                  </div>
                  <div class="intro-hero-btn-wrap">
                        <button class="intro-hero-btn" @click="storeData()"><span>Next</span></button>
                  </div>
            </section>
      </div>
</template>

<script>
      import BackButton from "../components/BackButton"
      import gaEventMixin from "../mixins/gaEventMixin"

      const memberPaysPercent = ['20%', '15%', '20%', '25%']
      const memberPaysOutPercent = ['out-of-network not covered', '40%', '40%', '40%']
      const fullIn = ['100%', '100%', '100%', '100%']
      const notCoveredOut = ['Not covered', 'Not covered', 'Not covered', 'Not covered']
      const coAfterDeductibleOut = ['Not covered', 'Co-insurance after deductible', 'Co-insurance after deductible', 'Co-insurance after deductible']
      const coAfterDeductibleSingle = ['Co-insurance after deductible', 'Co-insurance after deductible', 'Co-insurance after deductible']

      function buildCategories(deductible, oopMax, wellbeing) {
            return [
                  {
                        title: 'DEDUCTIBLE',
                        tooltip: 'The amount you’re responsible for paying each plan year before your plan begins to cover its portion of the covered cost',
                        metrics: [{ type: 'pair', in: deductible.in, out: deductible.out }]
                  },
                  {
                        title: 'CO-INSURANCE',
                        tooltip: 'The percentage you pay for the cost of covered health expenses after meeting your deductible.',
                        metrics: [
                              { type: 'pair', inLabel: 'in-network after deductible', outLabel: 'out-of-network after deductible', in: memberPaysPercent, out: memberPaysOutPercent }
                        ]
                  },
                  {
                        title: 'PREVENTIVE CARE',
                        metrics: [{ type: 'pair', in: fullIn, out: notCoveredOut }]
                  },
                  {
                        title: 'PHYSICIAN OFFICE SERVICES',
                        tooltip: 'Family practice, general practice, internal medicine, pediatrics and OB/GYN.',
                        metrics: [{
                              type: 'stack',
                              note: 'Illness/injury',
                              columns: [
                                    [
                                          { value: '$35 co-pay', label: 'in-network primary care providers (PCPs)' },
                                          { value: '$45 co-pay', label: 'for in-network specialists' },
                                          { value: 'Not covered', label: 'out-of-network' }
                                    ],
                                    [
                                          { value: '15%', label: 'in-network after deductible' },
                                          { value: '40%', label: 'out-of-network after deductible' }
                                    ],
                                    [
                                          { value: '20%', label: 'in-network after deductible' },
                                          { value: '40%', label: 'out-of-network after deductible' }
                                    ],
                                    [
                                          { value: '25%', label: 'in-network after deductible' },
                                          { value: '40%', label: 'out-of-network after deductible' }
                                    ]
                              ]
                        }]
                  },
                  {
                        title: 'OUT-OF-POCKET MAXIMUM',
                        tooltip: 'The most you’ll have to pay in a plan year for covered medical expenses, including deductibles, co-pays (HMO plan only), co-insurance, and pharmacy costs. If you reach the out-of-pocket maximum, your eligible expenses are covered 100% by the plan for the remainder of the plan year.',
                        metrics: [{ type: 'pair', in: oopMax.in, out: oopMax.out }]
                  },
                  {
                        title: 'DIAGNOSTIC BREAST SERVICES',
                        metrics: [{ type: 'pair', in: fullIn, out: coAfterDeductibleOut, note: 'Mammogram, ultrasound and MRI' }]
                  },
                  {
                        title: 'DIAGNOSTIC COLONOSCOPIES',
                        metrics: [{ type: 'pair', in: fullIn, out: coAfterDeductibleOut }]
                  },
                  {
                        title: 'VIRTUAL VISITS',
                        metrics: [{
                              type: 'pair',
                              inLabel: 'in-network',
                              outLabel: 'out-of-network',
                              in: ['100% after $15 co-pay', 'Co-insurance, no deductible', 'Co-insurance, no deductible', 'Co-insurance, no deductible'],
                              out: coAfterDeductibleOut
                        }]
                  },
                  {
                        title: 'CHIROPRACTIC VISIT',
                        metrics: [{ type: 'single', values: ['100% after $45 co-pay', ...coAfterDeductibleSingle], note: '20 visits per plan year' }]
                  },
                  {
                        title: 'EYE EXAM — ROUTINE',
                        metrics: [{ type: 'pair', in: fullIn, out: notCoveredOut, note: 'Limited to one exam every 24 months; not subject to deductible. 100% coverage for dilated retinal eye exams for diabetics once per calendar year.' }]
                  },
                  {
                        title: 'HOSPITAL SERVICES',
                        metrics: [{ type: 'single', values: ['Co-insurance after deductible', ...coAfterDeductibleSingle], note: 'Inpatient/outpatient' }]
                  },
                  {
                        title: 'MATERNITY CARE',
                        metrics: [{ type: 'single', values: ['100% after $35 co-pay', ...coAfterDeductibleSingle], note: 'Physician routine prenatal care, delivery and postnatal' }]
                  },
                  {
                        title: 'OUTPATIENT REHABILITATION THERAPY',
                        metrics: [{ type: 'single', values: ['100% after $25 co-pay', ...coAfterDeductibleSingle], note: 'Physical, speech, cardiac, occupational, pulmonary therapy (40 visits per therapy per plan year)' }]
                  },
                  {
                        title: 'EMERGENCY ROOM CARE — HOSPITAL',
                        metrics: [{ type: 'single', values: ['100% after $200 co-pay', ...coAfterDeductibleSingle] }]
                  },
                  {
                        title: 'URGENT CARE VISIT / RETAIL HEALTH CLINIC',
                        metrics: [{ type: 'single', values: ['100% after $35 co-pay', ...coAfterDeductibleSingle] }]
                  },
                  {
                        title: 'WELL-BEING INCENTIVE CREDITS',
                        type: 'wellbeing',
                        wellbeing
                  }
            ]
      }

      const benefitsByTier = {
            'You': buildCategories(
                  { in: ['$1,300', '$1,500', '$2,000', '$2,500'], out: ['Not covered', '$3,000', '$4,000', '$5,000'] },
                  { in: ['$4,000', '$4,000', '$5,000', '$6,000'], out: ['Not covered', '$8,000', '$10,000', '$12,000'] },
                  [{ hmo: '480', goldBase: '400', goldTotal: '880', silverBase: '200', silverTotal: '680', bronzeBase: '100', bronzeTotal: '580' }]
            ),
            'You-Spouse': buildCategories(
                  { in: ['$1,950', '$2,250', '$3,000', '$3,750'], out: ['Not covered', '$4,500', '$6,000', '$7,500'] },
                  { in: ['$6,500', '$6,000', '$7,500', '$9,000'], out: ['Not covered', '$12,000', '$15,000', '$18,000'] },
                  [{ hmo: '960', goldBase: '600', goldTotal: '1,560', silverBase: '300', silverTotal: '1,260', bronzeBase: '150', bronzeTotal: '1,110' }]
            ),
            'You-Child': buildCategories(
                  { in: ['$1,950', '$2,250', '$3,000', '$3,750'], out: ['Not covered', '$4,500', '$6,000', '$7,500'] },
                  { in: ['$6,500', '$6,000', '$7,500', '$9,000'], out: ['Not covered', '$12,000', '$15,000', '$18,000'] },
                  [{ hmo: '480', goldBase: '600', goldTotal: '1,080', silverBase: '300', silverTotal: '780', bronzeBase: '150', bronzeTotal: '630' }]
            ),
            'You-Family': buildCategories(
                  { in: ['$2,600', '$3,000', '$4,000', '$5,000'], out: ['Not covered', '$6,000', '$8,000', '$10,000'] },
                  { in: ['$9,000', '$8,000', '$10,000', '$12,000'], out: ['Not covered', '$16,000', '$20,000', '$24,000'] },
                  [{ hmo: '960', goldBase: '800', goldTotal: '1,760', silverBase: '400', silverTotal: '1,360', bronzeBase: '200', bronzeTotal: '1,160' }]
            )
      }

      export default {
            name: "SHBPBenefits",
            data() {
                  return {
                        isStuck: false,
                        activeTier: 'You',
                        activePlan: this.$store.state.planSelect,
                        tiers: [
                              { key: 'You', label: 'You' },
                              { key: 'You-Spouse', label: 'You + Spouse' },
                              { key: 'You-Child', label: 'You + Child(ren)' },
                              { key: 'You-Family', label: 'You + Family' }
                        ],
                        plans: [
                              { key: 'hmo', label: 'HMO' },
                              { key: 'gold', label: 'HRA Gold' },
                              { key: 'silver', label: 'HRA Silver' },
                              { key: 'bronze', label: 'HRA Bronze' }
                        ]
                  }
            },
            computed: {
                  categories() {
                        return benefitsByTier[this.activeTier]
                  }
            },
            methods: {
                  infoPop(title, text) {
                        let message = "<h6 class='text-left dark-blue-text fs18 fw600 mb8'>" + title + "</h6><p class='text-left mb0'>" + text + "</p>"
                        this.$swal({
                              html: message,
                              showCancelButton: false
                        })
                  },
                  planSelect(name) {
                        this.$store.commit("changePlan", name)
                        this.track("Choice", name + " Coverage")
                        this.activePlan = name
                        localStorage.setItem("antPlan", name)
                  },
                  storeData() {
                        let planSelected = this.$store.state.planSelect
                        let message = 'You must select a plan before proceeding to the next step.'
                        if (!planSelected) {
                              this.$swal({
                                    html: message,
                                    showCancelButton: false
                              })
                        }
                        else this.$router.push("/summary")
                  }
            },
            mounted() {
                  window.scrollTo(0, 0)
                  if (!this.$store.state.coverage || this.$store.state.coverage == "") this.activeTier = "You"
                  else if (this.$store.state.coverage == "You-Child(ren)") this.activeTier = "You-Child"
                  else this.activeTier = this.$store.state.coverage

                  this.stickyObserver = new IntersectionObserver(
                        ([entry]) => { this.isStuck = !entry.isIntersecting },
                        { rootMargin: '-68px 0px 0px 0px', threshold: 0 }
                  )
                  this.stickyObserver.observe(this.$refs.stickySentinel)
            },
            beforeUnmount() {
                  if (this.stickyObserver) this.stickyObserver.disconnect()
            },
            components: {
                  BackButton
            },
            mixins: [gaEventMixin]
      }
</script>

<style lang="scss">
#benefits {
      .benefits-hero {
            background-color: #ffffff;
            margin-top: 68px;
            padding: 120px 0 120px 0;
            position: relative;
            display: block;

            .back-btn {
                  left: 86px;
                  border-color: #1254E8;

                  &::before {
                        background-image: none;
                        background-color: #1254E8;
                        filter: none;
                        -webkit-mask-image: url("~@/assets/img/arrow-left-icon.png");
                        mask-image: url("~@/assets/img/arrow-left-icon.png");
                        -webkit-mask-size: 15px 13px;
                        mask-size: 15px 13px;
                        -webkit-mask-repeat: no-repeat;
                        mask-repeat: no-repeat;
                        -webkit-mask-position: center;
                        mask-position: center;
                  }
            }

            .grid-container.max {
                  width: 100%;
                  max-width: 85rem !important;
            }

            .benefits-header {
                  text-align: center;
                  max-width: 900px;
                  margin: 0 auto 48px auto;

                  h1 {
                        color: #1355E9;
                        font-size: clamp(2.5rem, 5vw, 4.25rem) !important;
                        font-weight: 600;
                        line-height: 1 !important;
                        margin-bottom: 24px;
                  }

                  .benefits-desc {
                        color: #012E58;
                        font-size: 1.125rem;
                        line-height: 1.5;
                        margin-bottom: 0;

                        a {
                              color: #1355E9;
                              font-weight: 700;
                              text-decoration: underline;
                        }
                  }
            }

            .benefits-tabs {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 32px;
                  position: sticky;
                  top: 68px;
                  z-index: 6;
                  background-color: #ffffff;
                  padding: 16px 0 8px 0;
            }

            .benefits-tab {
                  background: none;
                  border: none;
                  padding: 0 0 8px 0;
                  cursor: pointer;
                  font-family: inherit;
                  font-size: 0.75rem;
                  font-weight: 700;
                  letter-spacing: 0.02em;
                  text-transform: uppercase;
                  color: #b2b2b2;
                  border-bottom: solid 2px transparent;

                  &.active {
                        color: #012E58;
                        border-bottom-color: #012E58;
                  }

                  &:hover {
                        color: #012E58;
                  }
            }

            .benefits-sticky-sentinel {
                  height: 1px;
            }

            .benefits-plans-row-wrap {
                  position: sticky;
                  top: 113px;
                  z-index: 5;
                  background-color: #ffffff;
                  padding: 16px 0;
                  margin-bottom: 40px;
                  box-shadow: none;
                  transition: box-shadow 0.15s ease-in-out;

                  &.is-stuck {
                        box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.06);
                  }
            }

            .benefits-plans-row {
                  display: grid;
                  grid-template-columns: repeat(4, 1fr);
                  gap: 16px;
            }

            .benefits-plan-pill {
                  background-color: #e2ecf8;
                  color: #012E58;
                  font-weight: 700;
                  font-size: 0.9375rem;
                  text-align: center;
                  padding: 14px 12px;
                  border-radius: 30px;
                  border: none;
                  cursor: pointer;
                  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
                  font-family: inherit;

                  &:hover,
                  &.active {
                        background-color: #1355E9;
                        color: #ffffff;
                  }
            }

            .benefits-category {
                  margin-bottom: 40px;
            }

            .benefits-category-title {
                  color: #12130f;
                  font-size: 0.9375rem;
                  font-weight: 700;
                  letter-spacing: 0.02em;
                  text-transform: uppercase;
                  margin-bottom: 12px;

                  .info-circle {
                        position: relative;
                        cursor: pointer;
                        display: inline-block;
                        width: 14px;
                        height: 14px;

                        &::before {
                              content: "";
                              background-image: url("~@/assets/img/info-icon.png");
                              position: absolute;
                              top: 2px;
                              left: 0;
                              width: 12px;
                              height: 12px;
                              background-repeat: no-repeat;
                              background-size: 12px 12px;
                        }
                  }
            }

            .benefits-rule {
                  border: none;
                  border-top: solid 1px #d8dce0;
                  margin: 0 0 24px 0;
            }

            .benefits-metric {
                  margin-bottom: 20px;

                  &:last-child {
                        margin-bottom: 0;
                  }
            }

            .benefits-metric-label {
                  color: #12130f;
                  font-size: 0.875rem;
                  font-weight: 700;
                  margin-bottom: 12px;
            }

            .benefits-grid {
                  display: grid;
                  grid-template-columns: repeat(4, 1fr);
                  gap: 16px;
            }

            .benefits-col {
                  text-align: center;
            }

            .benefits-value {
                  color: #1355E9;
                  font-size: 1rem;
                  font-weight: 700;
                  margin-bottom: 16px;
                  line-height: 1.3;

                  &:last-child {
                        margin-bottom: 0;
                  }
            }

            .benefits-sub {
                  display: block;
                  color: #12130f;
                  font-size: 0.875rem;
                  font-weight: 400;
                  margin-top: 2px;
            }

            .benefits-note {
                  text-align: center;
                  color: #6b6b6b;
                  font-size: 0.75rem;
                  margin: 8px 0 0 0;
            }

            .benefits-wellbeing-block {
                  margin-bottom: 24px;

                  &:last-child {
                        margin-bottom: 0;
                  }
            }

            .benefits-wellbeing-grid {
                  display: grid;
                  grid-template-columns: repeat(4, 1fr);
                  gap: 16px 16px;
            }

            .benefits-wellbeing-item {
                  text-align: center;
                  margin-bottom: 20px;
            }

            .benefits-disclaimer {
                  margin-top: 60px;
                  padding-top: 24px;
                  border-top: solid 1px #d8dce0;

                  ol {
                        margin-left: 1.25rem;
                        padding: 0;

                        li {
                              color: #6b6b6b;
                              font-size: 0.75rem;
                              line-height: 1.5;
                              margin-bottom: 4px;
                        }
                  }

                  .benefits-fineprint {
                        color: #6b6b6b;
                        font-size: 0.75rem;
                        line-height: 1.5;
                        margin: 16px 0 0 0;
                  }
            }

            .intro-hero-btn-wrap {
                  position: fixed;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  z-index: 3;
                  background-color: #ffffff;
                  box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.10);
                  padding: 20px 0;
                  text-align: center;
            }

            .intro-hero-btn {
                  display: block;
                  background-color: #1355E9;
                  color: #ffffff !important;
                  font-weight: 700;
                  text-transform: uppercase;
                  font-size: 1rem;
                  padding: 18px 0;
                  width: 100%;
                  max-width: 330px;
                  text-align: center;
                  border-radius: 30px;
                  border: none;
                  cursor: pointer;
                  margin: 0 auto;

                  &:hover {
                        background-color: #44B8F3;
                  }
            }

            @media only screen and (max-width: 959px) {
                  padding: 96px 24px 100px 24px;

                  .back-btn {
                        left: 2rem;
                  }

                  .benefits-header {
                        text-align: left;

                        h1 {
                              font-size: clamp(2rem, 8vw, 2.75rem) !important;
                        }
                  }

                  .benefits-tabs {
                        gap: 12px;
                        flex-wrap: wrap;
                  }

                  .benefits-tab {
                        white-space: normal;
                        font-size: 0.625rem;
                  }

                  .benefits-table-scroll {
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin: 0 -24px;
                        padding: 0 24px;
                  }

                  .benefits-plans-row-wrap {
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin: 0 -24px 40px -24px;
                        padding: 16px 24px;
                  }

                  .benefits-plans-row {
                        grid-template-columns: repeat(4, 130px);
                        min-width: 568px;
                        gap: 12px;
                  }

                  .benefits-plan-pill {
                        font-size: 0.75rem;
                        padding: 10px 8px;
                  }

                  .benefits-category {
                        min-width: 568px;
                  }

                  .benefits-category-title {
                        position: sticky;
                        left: 0;
                        z-index: 2;
                        background-color: #ffffff;
                        display: inline-block;
                        padding-right: 12px;
                  }

                  .benefits-metric-label {
                        position: sticky;
                        left: 0;
                        z-index: 2;
                        background-color: #ffffff;
                        display: inline-block;
                        padding-right: 12px;
                  }

                  .benefits-grid {
                        grid-template-columns: repeat(4, 130px);
                        gap: 20px 12px;
                  }

                  .benefits-col {
                        text-align: left;
                  }

                  .benefits-note {
                        text-align: left;
                  }

                  .benefits-wellbeing-grid {
                        grid-template-columns: repeat(4, 130px);
                        gap: 20px 12px;
                  }

                  .benefits-wellbeing-item {
                        text-align: left;
                  }
            }
      }
}
</style>
